import React, { ForwardedRef, forwardRef, InputHTMLAttributes, MouseEvent, useRef } from 'react';
import { ThemeColorKeys } from '../../base/useTheme';
import { SwitchInput, SwitchThumb, SwitchTrack } from './Switch.style';

type SwitchAttributes = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'checked' | 'children' | 'color' | 'value' | 'type' | 'ref'
>;

interface SwitchProps extends SwitchAttributes {
    $color?: ThemeColorKeys;
    checked?: boolean;
}

function SwitchInner(props: SwitchProps, ref: ForwardedRef<HTMLInputElement>) {
    const inputRef = useRef<HTMLInputElement>();
    const { checked = false, disabled = false, style = {}, className, ...rest } = props;

    function handleClick(event: MouseEvent<HTMLDivElement>) {
        if (disabled) {
            return;
        }

        if (event.target !== inputRef.current && inputRef.current) {
            event.stopPropagation();
            inputRef.current.click();
            return;
        }
    }

    function handleMouseDown(event: MouseEvent<HTMLDivElement>) {
        if (disabled) {
            return;
        }

        if (event.target !== inputRef.current && inputRef.current) {
            event.stopPropagation();
            inputRef.current.focus({ preventScroll: true });
            return;
        }
    }

    function initRefs(instance: HTMLInputElement) {
        inputRef.current = instance;

        if (typeof ref === 'function') {
            ref(instance);
        }

        if (typeof ref === 'object' && ref !== null) {
            ref.current = instance;
        }
    }

    return (
        <SwitchTrack
            style={style}
            className={className}
            $checked={checked}
            $disabled={disabled}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
        >
            <SwitchThumb />
            <SwitchInput {...rest} ref={initRefs} type="checkbox" checked={checked} disabled={disabled} />
        </SwitchTrack>
    );
}
export const Switch = forwardRef(SwitchInner);
